import React, { useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { useForm } from "react-hook-form";

export const PREVIEW_LIMIT = 100;

/** Column labels contain spaces, and one label can appear in several groups. */
const columnId = (group, column) =>
  `export-column-${`${group}-${column}`.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`;

const ExportInfo = ({
  properties = [],
  propertyCount,
  previewPage = 1,
  previewPages = 1,
  onPreviewPageChange = () => {},
  onPreviewRetry = () => {},
  isPreviewLoading = false,
  previewError = false,
  availableColumns = [],
  columnGroups = [],
  defaultColumns = [],
  phoneFilters = null,
  handleExportPdf,
  isFormProcessing,
  total_available_credits,
}) => {
  const {
    register,
    watch,
    getValues,
    setValue,
    handleSubmit,
    formState: { errors },
  } = useForm({
    defaultValues: {
      file_type: "csv",
      columns: [],
      notify_by_email: false,
      phone_type: "all",
      include_dnc: true,
    },
  });
  const values = watch();
  const totalProperties = propertyCount ?? properties.length;

  const previewStart = (previewPage - 1) * PREVIEW_LIMIT;

  // An empty list means the API did not advertise groups; `ungrouped` below
  // then provides the safe fallback for older backends.
  const groups = columnGroups;

  // Only used when the API has not sent groups - an older backend, or a failed
  // options call. It is deliberately NOT treated as one free group: doing so
  // ticked every contact column by default and would have spent a credit per
  // property without the user ever asking.
  const ungrouped = useMemo(
    () => (groups.length === 0 ? availableColumns : []),
    [groups, availableColumns]
  );

  useEffect(() => {
    if (defaultColumns.length === 0 || getValues("columns").length > 0) return;

    // The backend owns what is ticked on open - owner name, address and the two
    // contact kinds - so the default cannot drift between the two pipelines.
    // Everything else stays available but unticked: the previous default
    // selected every free field, burying a call list under parcel data.
    setValue("columns", [...new Set(defaultColumns)]);
  }, [defaultColumns, getValues, setValue]);

  // The backend owns the phone choices and their defaults, like the columns.
  // An older backend sends none, and then the screen offers no choice at all.
  const phoneTypes = phoneFilters?.phone_types || [];
  const offersPhoneChoices = phoneTypes.length > 0;
  const phoneDefaults = phoneFilters?.defaults;

  useEffect(() => {
    if (!phoneDefaults) return;
    setValue("phone_type", phoneDefaults.phone_type);
    setValue("include_dnc", phoneDefaults.include_dnc);
  }, [phoneDefaults, setValue]);

  const wantsPhones = (values.columns || []).includes("Phone Numbers");
  // A column may sit in several groups; the controls (and their ids) belong to
  // the first one only.
  const phoneGroupIndex = groups.findIndex((group) => group.columns.includes("Phone Numbers"));
  const narrowsPhones =
    offersPhoneChoices &&
    wantsPhones &&
    phoneDefaults &&
    (values.phone_type !== phoneDefaults.phone_type ||
      Boolean(values.include_dnc) !== phoneDefaults.include_dnc);

  useEffect(() => {
    if (getValues("name")) return;
    // A sensible, editable default so the field is never the thing standing
    // between the user and their export.
    const today = new Date().toISOString().slice(0, 10);
    setValue("name", `Export ${totalProperties} properties ${today}`);
  }, [totalProperties, getValues, setValue]);

  // Registered once, outside the rendering branches. It used to be attached
  // only to the checkbox groups, so an API returning nothing but `sets` groups
  // would leave the field unvalidated and allow an empty export.
  const columnsField = register("columns", {
    validate: (selected) =>
      (Array.isArray(selected) && selected.length > 0) || "Select at least one column.",
  });

  const submit = (data) => {
    // Sent only alongside the phone column, so an export without numbers - or
    // one to a backend that offered no choice - carries no phone settings.
    const phoneChoices =
      offersPhoneChoices && data.columns.includes("Phone Numbers")
        ? { phone_type: data.phone_type, include_dnc: Boolean(data.include_dnc) }
        : {};

    handleExportPdf({
      name: data.name,
      file_type: data.file_type,
      columns: data.columns,
      notify_by_email: Boolean(data.notify_by_email),
      ...phoneChoices,
    });
  };

  return (
    <>
      <div className="top_strip_export">
        <div className="contain-fluid">
          <div className="flex">
            <Link to="#" onClick={() => window.history.back()}>
              <img src="/images/back_arrow.svg" alt="" />
              <span>Export</span>
            </Link>
            <div className="label_info">
              <small>{total_available_credits || 0} export credits available</small>
              <div className="pro_ress" />
            </div>
          </div>
        </div>
      </div>

      {totalProperties > 0 ? (
        <section className="export_sec">
          <div className="contain-fluid">
            <form onSubmit={handleSubmit(submit)}>
              <div className="row form_row field_row cell_form_row">
                <div className="col-md-5">
                  <div className="form_blk">
                    {/* h6, not label: the sibling "Properties to Export" field uses
                        one and renders correctly, while a <label> here was drawn
                        inside the input's own box. aria-label keeps the field
                        named for assistive tech and for tests. */}
                    <h6 className="lbl_heading">Name</h6>
                    <input
                      id="export-name"
                      type="text"
                      className="input"
                      aria-label="Name"
                      {...register("name", { required: "Required." })}
                    />
                    <span className="validation-error">{errors.name?.message}</span>
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="form_blk">
                    <h6 className="lbl_heading">Properties to Export</h6>
                    {/* Named for assistive tech, like the Name field beside it:
                        the visible heading is an h6, not a label. */}
                    <input
                      type="number"
                      className="input"
                      aria-label="Properties to Export"
                      value={totalProperties}
                      readOnly
                    />
                  </div>
                </div>
              </div>

              <div className="br" />

              <div className="file_output_row">
                <h6 className="lbl_heading">File Output</h6>
                <div className="btn_lbl_blk">
                  {["excel", "csv"].map((type) => (
                    <button
                      key={type}
                      type="button"
                      className={values.file_type === type ? "btn_lbl active" : "btn_lbl"}
                      onClick={() => setValue("file_type", type)}
                    >
                      {type === "excel" ? "Excel (XLSX)" : "CSV"}
                    </button>
                  ))}
                </div>
              </div>

              <div className="br" />

              <div className="lbl_btn">
                <input
                  id="notify-export-by-email"
                  type="checkbox"
                  {...register("notify_by_email")}
                />
                <label htmlFor="notify-export-by-email">Email me the file when it is ready</label>
              </div>

              <div className="br" />

              <div className="property_fields">
              {totalProperties > 0 && (
                <div className="preview_blk">
                  <h6 className="lbl_heading">Preview</h6>
                  {isPreviewLoading ? (
                    <p role="status">Loading preview...</p>
                  ) : previewError ? (
                    <p>
                      Preview could not be loaded. {" "}
                      <button type="button" className="site_btn blank sm" onClick={onPreviewRetry}>
                        Retry
                      </button>
                    </p>
                  ) : properties.length > 0 ? (
                    <>
                      <p>
                        Showing {(previewStart + 1).toLocaleString()}
                        &ndash;{Math.min(previewStart + properties.length, totalProperties).toLocaleString()}
                        {" of "}{totalProperties.toLocaleString()} selected properties.
                      </p>
                      <div className="outer_table">
                        <table>
                          <thead><tr><th>Parcel ID</th><th>Address</th><th>Link</th></tr></thead>
                          <tbody>
                            {properties.map((property) => (
                              <tr key={property.id}>
                                <td>{property.parcel_id}</td>
                                <td>{property.address}</td>
                                <td><Link to={`/property-details/${property.id}`} target="_blank" rel="noopener noreferrer">View</Link></td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </>
                  ) : (
                    <p>No preview rows are available for this page.</p>
                  )}
                  {previewPages > 1 && (
                    <div className="preview_pager">
                      {/* type="button": inside the export form, a bare button
                          submits it, so paging the preview would have started
                          the export. */}
                      <button
                        type="button"
                        className="site_btn blank sm"
                        disabled={isPreviewLoading || previewPage === 1}
                        onClick={() => onPreviewPageChange(Math.max(1, previewPage - 1))}
                      >
                        Previous
                      </button>
                      <span>Page {previewPage} of {previewPages}</span>
                      <button
                        type="button"
                        className="site_btn blank sm"
                        disabled={isPreviewLoading || previewPage >= previewPages}
                        onClick={() => onPreviewPageChange(Math.min(previewPages, previewPage + 1))}
                      >
                        Next
                      </button>
                    </div>
                  )}
                </div>
              )}
                {ungrouped.length > 0 && (
                  <div className="export_column_group">
                    <div className="export_column_group_head"><h6>Columns</h6></div>
                    <div className="check_flex">
                      <div className="check_inner">
                        {ungrouped.map((column) => (
                          <div key={column} className="lbl_btn">
                            <input
                              id={columnId("all", column)}
                              type="checkbox"
                              value={column}
                              {...columnsField}
                            />
                            <label htmlFor={columnId("all", column)}>{column}</label>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
                {groups.map((group, groupIndex) => {
                  const groupId = group.key ?? groupIndex;
                  const selectedInGroup = group.columns.filter(
                    (column) => (values.columns || []).includes(column)
                  ).length;
                  const allSelected = selectedInGroup === group.columns.length;

                  return (
                    <div key={groupId} className="export_column_group">
                      <div className="export_column_group_head">
                        <h6>{group.label}</h6>
                        <button
                          type="button"
                          className="site_btn blank yellow_blank sm"
                          onClick={() => {
                            const others = (getValues("columns") || []).filter(
                              (column) => !group.columns.includes(column)
                            );
                            setValue(
                              "columns",
                              allSelected ? others : [...others, ...group.columns],
                              { shouldValidate: true }
                            );
                          }}
                        >
                          {allSelected ? "Clear" : "Select all"}
                        </button>
                      </div>
                      {group.costs_credits && (
                        <>
                          <p className="export_column_group_note">
                            Uses one credit per property with a new contact. Contacts you
                            already own are free.
                          </p>
                          <p className="export_column_group_note">
                            {narrowsPhones
                              ? "Every address we hold is exported, but only the phone numbers that match your choices below - each with its type and Do Not Call status."
                              : "Every number and address we hold is exported - each phone number with its type and Do Not Call status."}
                          </p>
                        </>
                      )}
                      <div className="check_flex">
                        <div className="check_inner">
                          {group.columns.map((column) => (
                            <div key={column} className="lbl_btn">
                              <input
                                id={columnId(groupId, column)}
                                type="checkbox"
                                value={column}
                                {...columnsField}
                              />
                              <label htmlFor={columnId(groupId, column)}>{column}</label>
                            </div>
                          ))}
                        </div>
                      </div>
                      {groupIndex === phoneGroupIndex && offersPhoneChoices && wantsPhones && (
                        <div className="export_phone_choices">
                          <div className="file_output_row">
                            <h6 className="lbl_heading" id="export-phone-type">Phone type</h6>
                            <div className="btn_lbl_blk" role="group" aria-labelledby="export-phone-type">
                              {phoneTypes.map((type) => (
                                <button
                                  key={type.value}
                                  type="button"
                                  aria-pressed={values.phone_type === type.value}
                                  className={values.phone_type === type.value ? "btn_lbl active" : "btn_lbl"}
                                  onClick={() => setValue("phone_type", type.value)}
                                >
                                  {type.label}
                                </button>
                              ))}
                            </div>
                          </div>
                          <div className="lbl_btn">
                            <input id="export-include-dnc" type="checkbox" {...register("include_dnc")} />
                            <label htmlFor="export-include-dnc">Include Do Not Call (DNC) numbers</label>
                          </div>
                          {narrowsPhones && (
                            <p className="export_column_group_note">
                              Credits are still charged per property, so a property can use a
                              credit even if none of its numbers match. Numbers left out stay
                              in your account and are free in a later export.
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
                <span className="validation-error">{errors.columns?.message}</span>
              </div>


              <div className="btn_blk">
                <button
                  className="site_btn"
                  type="submit"
                  disabled={isFormProcessing || availableColumns.length === 0}
                >
                  Export {isFormProcessing && <i className="spinner" />}
                </button>
              </div>
            </form>
          </div>
        </section>
      ) : (
        <section className="export_sec">
          <div className="contain-fluid">
            {/* Not an error. Reaching /export without a selection is an
                ordinary thing to do - a bookmark, a reopened tab - so it says
                what to do next and offers the way there. */}
            <h3>Nothing selected yet</h3>
            <p className="export_progress_line">
              Choose the properties you want on the search page, then select
              Export.
            </p>
            <div className="btn_blk">
              <Link className="site_btn" to="/search-property">
                Go to search
              </Link>
            </div>
          </div>
        </section>
      )}
    </>
  );
};

export default ExportInfo;
